import {
  Button,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Textarea,
} from "@mui/joy";
import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sanitizeHtml } from "../Helpers";

interface DynamicEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  values: FormData[];
}
interface FormData {
  value: string;
  label: string;
  property?: string;
  error?: string;
}

const hasHtmlContent = (value: string): boolean => {
  return !!value && /<[a-z][\s\S]*>/i.test(value);
};

const RichTextEditor = ({ data, index, onChange }: {
  data: FormData;
  index: number;
  onChange: (value: string, index: number, property?: string) => void;
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML, index, data.property);
    }
  };

  return (
    <div
      ref={editorRef}
      contentEditable
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.value) }}
      onInput={handleInput}
      style={{
        padding: '8px 12px',
        border: '1px solid var(--color-box-border, #ccc)',
        borderRadius: 'var(--joy-radius-sm, 6px)',
        minHeight: '60px',
        maxHeight: '300px',
        overflowY: 'auto',
        backgroundColor: 'var(--color-input-bg, #fff)',
        color: 'var(--color-text, #000)',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  );
};

const DynamicEditModal: React.FC<DynamicEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  values,
}) => {
  const [formData, setFormData] = useState<FormData[]>([]);
  useEffect(() => {
    setFormData(values);
  }, [values]);
  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    index: number,
    property?: string
  ) => {
    const { name, value } = e.target;
    updateFormData(value, index, name, property);
  };

  const handleRichTextChange = (
    value: string,
    index: number,
    property?: string
  ) => {
    const name = formData[index]?.label || "";
    updateFormData(value, index, name, property);
  };

  const updateFormData = (
    value: string,
    index: number,
    name: string,
    property?: string
  ) => {
    const errors: Partial<Record<string, string>> = {};
    const strippedValue = value.replace(/<[^>]*>/g, '').trim();
    if (!strippedValue) {
      errors[name] = "Dit veld is verplicht.";
    }

    setFormData((prevData: FormData[]) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        value: value,
        error: errors[name],
        property: property,
      };
      return [...newData];
    });
  };
  const handleSave = () => {
    const hasErrors = formData.some((item) => !!item.error);

    if (!hasErrors) {
      onSave(formData);
      onClose();
    } else {
      toast.error("Gelieve alle velden in te vullen.");
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <React.Fragment>
        <ModalDialog>
          <div className="modal">
            <h3>Gegevens aanpassen</h3>
            <form className="formModal">
              {formData.map((data, index) => (
                <div key={index}>
                  <FormLabel
                    sx={{ marginTop: "1rem", paddingBottom: "0.5rem" }}
                  >
                    {data.label}
                  </FormLabel>
                  {hasHtmlContent(data.value) ? (
                    <RichTextEditor
                      data={data}
                      index={index}
                      onChange={handleRichTextChange}
                    />
                  ) : (
                    <Textarea
                      name={data.label}
                      defaultValue={data.value}
                      minRows={1}
                      maxRows={10}
                      onChange={(e) =>
                        handleInputChange(e, index, data?.property)
                      }
                    />
                  )}
                  <div style={{ color: "red" }}>{data.error}</div>
                </div>
              ))}
              <div style={{ display: "flex", gap: "8px", marginTop: "1rem" }}>
                <Button
                  variant="outlined"
                  color="neutral"
                  className="headerButton"
                  type="button"
                  onClick={handleSave}
                >
                  Opslaan
                </Button>
                <Button
                  variant="outlined"
                  color="neutral"
                  className="headerButton"
                  type="button"
                  onClick={onClose}
                >
                  Annuleer
                </Button>
              </div>
            </form>
          </div>
        </ModalDialog>
        <ToastContainer />
      </React.Fragment>
    </Modal>
  );
};

export default DynamicEditModal;
